import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const DemographicsPanel = () => {
  const [activeTab, setActiveTab] = useState('age');

  const ageData = [
    { name: '0-18', value: 15, count: 427, color: '#3B82F6' },
    { name: '19-35', value: 28, count: 797, color: '#10B981' },
    { name: '36-50', value: 32, count: 911, color: '#F59E0B' },
    { name: '51-65', value: 18, count: 512, color: '#EF4444' },
    { name: '65+', value: 7, count: 200, color: '#8B5CF6' }
  ];

  const genderData = [
    { name: 'Female', value: 54, count: 1537, color: '#EC4899' },
    { name: 'Male', value: 44, count: 1252, color: '#3B82F6' },
    { name: 'Other', value: 2, count: 58, color: '#6B7280' }
  ];

  const conditionData = [
    { condition: 'Hypertension', patients: 892, percentage: 31 },
    { condition: 'Diabetes', patients: 743, percentage: 26 },
    { condition: 'Heart Disease', patients: 521, percentage: 18 },
    { condition: 'Respiratory', patients: 387, percentage: 14 },
    { condition: 'Mental Health', patients: 304, percentage: 11 }
  ];

  const riskStratification = [
    { level: 'Low Risk', count: 1456, percentage: 51, color: 'var(--color-success)' },
    { level: 'Medium Risk', count: 982, percentage: 34, color: 'var(--color-warning)' },
    { level: 'High Risk', count: 409, percentage: 15, color: 'var(--color-destructive)' }
  ];

  const tabs = [
    { id: 'age', label: 'Age Groups', icon: 'Users' },
    { id: 'gender', label: 'Gender', icon: 'User' },
    { id: 'conditions', label: 'Conditions', icon: 'Activity' },
    { id: 'risk', label: 'Risk Levels', icon: 'Shield' }
  ];

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="500"
      >
        {`${(percent * 100)?.toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg medical-shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Patient Demographics</h3>
            <p className="text-sm text-text-secondary">Population analysis and risk stratification</p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Download" size={16} className="text-text-secondary cursor-pointer hover:text-primary" />
            <Icon name="RefreshCw" size={16} className="text-text-secondary cursor-pointer hover:text-primary" />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium medical-transition ${
                activeTab === tab?.id
                  ? 'bg-background text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {/* Age Groups */}
        {activeTab === 'age' && (
          <div>
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={ageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {ageData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}% (${props?.payload?.count} patients)`, 'Percentage']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {ageData?.map((item) => (
                <div key={item?.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }} />
                    <span className="text-sm font-medium text-text-primary">{item?.name} years</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-text-primary">{item?.count?.toLocaleString()}</div>
                    <div className="text-xs text-text-secondary">{item?.value}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gender Distribution */}
        {activeTab === 'gender' && (
          <div>
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {genderData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}% (${props?.payload?.count} patients)`, 'Percentage']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {genderData?.map((item) => (
                <div key={item?.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }} />
                    <span className="text-sm font-medium text-text-primary">{item?.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-text-primary">{item?.count?.toLocaleString()}</div>
                    <div className="text-xs text-text-secondary">{item?.value}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Medical Conditions */}
        {activeTab === 'conditions' && (
          <div>
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={conditionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="condition" 
                    tick={{ fontSize: 12 }}
                    stroke="var(--color-text-secondary)"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="var(--color-text-secondary)"
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value} patients`, 'Count']}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                  />
                  <Bar dataKey="patients" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {conditionData?.map((item) => (
                <div key={item?.condition} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary">{item?.condition}</span>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-text-primary">{item?.patients?.toLocaleString()}</div>
                    <div className="text-xs text-text-secondary">{item?.percentage}% of patients</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk Stratification */}
        {activeTab === 'risk' && (
          <div>
            <div className="space-y-4 mb-6">
              {riskStratification?.map((item) => (
                <div key={item?.level} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }} />
                      <span className="text-sm font-medium text-text-primary">{item?.level}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-text-primary">{item?.count?.toLocaleString()}</div>
                      <div className="text-xs text-text-secondary">{item?.percentage}%</div>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full medical-transition"
                      style={{
                        width: `${item?.percentage}%`,
                        backgroundColor: item?.color
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h4 className="font-medium text-text-primary mb-2">Risk Assessment Criteria</h4>
              <div className="space-y-1 text-sm text-text-secondary">
                <div>• <strong>Low Risk:</strong> Stable vitals, routine care</div>
                <div>• <strong>Medium Risk:</strong> Chronic conditions, regular monitoring</div>
                <div>• <strong>High Risk:</strong> Critical conditions, intensive care required</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemographicsPanel;